import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Shared pure-render pricing surface.
 *
 * Takes catalog data (`plans`, `addons`, `agency_plans`, `free_sites`),
 * a theme-tokens object and an optional `copy` deck. Renders the
 * whole public page: hero head + plan cards + assurance strip +
 * compare table + "every limit defined" + install matrix + standards
 * band + agency band + fine print.
 *
 * Numbers always come from the data. Static copy (labels, tooltips,
 * install matrix, standards band) comes from the deck; the marketing
 * app passes its own deck, the ops admin preview uses the default.
 *
 * Carries no fetching, no routing, no theming internals — the
 * consuming app wraps this with its own header / footer / theme
 * provider. Same component renders on `sill.so/pricing` and inside
 * ops2026 as the staff preview.
 */
import { useMemo, useState } from 'react';
import { mono, sans, serif } from '../fonts.js';
import { DEFAULT_COPY_DECK, isInternalDescription, resolvePitch, resolveAgencyPitch, labelFromSlug, } from './copy-deck.js';
import { formatCount, formatLimit, formatMoneyMinor, formatPrice, hasYearly, pageLoadsRatioClause, phraseForMultiple, pickPrice, } from './format.js';
import { PricingCards } from './PricingCards.js';
import { CompareTable } from './CompareTable.js';
import { AgencyBand } from './AgencyBand.js';
import { StandardsBand } from './StandardsBand.js';
import { InstallMatrix } from './InstallMatrix.js';
import { LimitsBlock } from './LimitsBlock.js';
import { FinePrint } from './FinePrint.js';
export { formatPrice, formatLimit, formatCount, formatMoneyMinor, hasYearly, pickPrice, pageLoadsRatioClause, phraseForMultiple, DEFAULT_COPY_DECK, isInternalDescription, resolvePitch, resolveAgencyPitch, labelFromSlug, };
export function PricingTable(props) {
    const { data, theme, copy = DEFAULT_COPY_DECK, showDraftBadges = false, agenciesHref = '/agencies', ctaHref = 'https://app.sill.so/sign-up', monthsFreeOnYearly = 2, } = props;
    const yearlyAvailable = useMemo(() => hasYearly(data), [data]);
    const [interval, setInterval] = useState('monthly');
    const effectiveInterval = yearlyAvailable ? interval : 'monthly';
    const agencyPlans = data.agency_plans ?? [];
    if (data.plans.length === 0 && data.addons.length === 0 && agencyPlans.length === 0) {
        return (_jsxs(Section, { theme: theme, id: "pricing-empty", children: [_jsx("div", { style: {
                        ...mono,
                        fontSize: 10,
                        letterSpacing: 2,
                        textTransform: 'uppercase',
                        color: theme.muted,
                        marginBottom: 12,
                    }, children: "Pricing" }), _jsx("h1", { style: {
                        ...serif,
                        fontSize: 'clamp(32px, 6vw, 48px)',
                        lineHeight: 1.05,
                        margin: 0,
                        letterSpacing: -1.5,
                        color: theme.text,
                    }, children: "Pricing is being finalised." }), _jsx("p", { style: {
                        ...sans,
                        fontSize: 16,
                        color: theme.body,
                        maxWidth: 640,
                        marginTop: 16,
                    }, children: "The plan details will be live here once the Sill catalog is published. In the meantime, the agent readiness scan is free and Sill Discovery works on every site." })] }));
    }
    const freeSitesLine = buildFreeSitesLine(data.free_sites, copy);
    return (_jsxs(Section, { theme: theme, id: "pricing", children: [_jsx("div", { style: {
                    ...mono,
                    fontSize: 10,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    color: theme.muted,
                    marginBottom: 12,
                }, children: "Pricing" }), _jsx("h1", { style: {
                    ...serif,
                    fontSize: 'clamp(36px, 6vw, 52px)',
                    lineHeight: 1.04,
                    margin: 0,
                    letterSpacing: -1,
                    color: theme.text,
                    textWrap: 'balance',
                }, children: copy.headline }), _jsx("p", { style: {
                    ...sans,
                    fontSize: 16,
                    color: theme.body,
                    maxWidth: 640,
                    marginTop: 12,
                    marginBottom: 24,
                }, children: freeSitesLine ?? copy.subline }), yearlyAvailable ? (_jsx("div", { role: "radiogroup", "aria-label": "Billing interval", style: {
                    display: 'inline-flex',
                    border: `1px solid ${theme.border}`,
                    borderRadius: 3,
                    padding: 2,
                    marginBottom: 24,
                    background: theme.surface,
                    alignItems: 'center',
                }, children: ['monthly', 'yearly'].map((it) => {
                    const active = effectiveInterval === it;
                    return (_jsx("button", { type: "button", role: "radio", "aria-checked": active, onClick: () => setInterval(it), style: {
                            ...sans,
                            background: active ? theme.text : 'transparent',
                            color: active ? theme.bg : theme.body,
                            border: 'none',
                            padding: '6px 14px',
                            cursor: 'pointer',
                            fontSize: 13,
                            borderRadius: 2,
                            minHeight: 32,
                        }, children: it === 'monthly'
                            ? 'Monthly'
                            : (_jsxs(_Fragment, { children: ["Yearly", ' ', _jsxs("span", { style: {
                                            ...mono,
                                            fontSize: 11,
                                            background: active ? theme.lime : theme.limeDim,
                                            color: active ? theme.ink : theme.limeFg,
                                            padding: '1px 6px',
                                            borderRadius: 2,
                                            marginLeft: 6,
                                        }, children: [monthsFreeOnYearly, " months free"] })] })) }, it));
                }) })) : null, _jsxs("p", { style: {
                    ...sans,
                    fontSize: 14,
                    color: theme.muted,
                    margin: '0 0 24px',
                }, children: [copy.scanline, ' ', _jsx("a", { href: copy.scanlineHref, style: { color: theme.text, textDecoration: 'underline', textDecorationColor: theme.border, textUnderlineOffset: 3 }, children: "Scan your site" })] }), data.plans.length > 0 ? (_jsx(PricingCards, { plans: data.plans, theme: theme, interval: effectiveInterval, copy: copy, ctaHref: ctaHref, showDraftBadges: showDraftBadges, monthsFreeOnYearly: monthsFreeOnYearly })) : null, _jsxs("div", { style: {
                    marginTop: 22,
                    display: 'grid',
                    gap: 10,
                    justifyItems: 'center',
                    textAlign: 'center',
                }, children: [_jsx("p", { style: { ...sans, color: theme.text, fontSize: 15, margin: 0, maxWidth: 70 * 8 }, children: copy.assureLine }), _jsx("p", { style: { ...sans, color: theme.muted, fontSize: 14, margin: 0 }, children: copy.assureSub }), _jsx("a", { href: "#agency", style: {
                            ...sans,
                            display: 'inline-flex',
                            gap: 8,
                            alignItems: 'center',
                            fontSize: 14,
                            color: theme.body,
                            textDecoration: 'none',
                            border: `1px solid ${theme.border}`,
                            borderRadius: 999,
                            padding: '6px 14px',
                            background: theme.surface,
                        }, children: agencyPlans.length === 0 ? copy.agencyLinkNoTiers : copy.agencyLink })] }), data.plans.length > 0 ? (_jsx(CompareTable, { plans: data.plans, theme: theme, interval: effectiveInterval, copy: copy, ctaHref: ctaHref, monthsFreeOnYearly: monthsFreeOnYearly, freeSites: data.free_sites, addons: data.addons })) : null, _jsx("div", { style: { marginTop: 64 }, children: _jsx(LimitsBlock, { theme: theme, copy: copy, data: data }) }), _jsx("div", { style: { marginTop: 64 }, children: _jsx(AgencyBand, { agencyPlans: agencyPlans, sitePlans: data.plans, theme: theme, interval: effectiveInterval, copy: copy, agenciesHref: agenciesHref, monthsFreeOnYearly: monthsFreeOnYearly, signupEnabled: data.agency_signup === true && agencyPlans.length > 0, ctaHref: ctaHref }) }), _jsx("div", { style: { marginTop: 64 }, children: _jsx(StandardsBand, { theme: theme, copy: copy }) }), _jsx("div", { style: { marginTop: 64 }, children: _jsx(InstallMatrix, { theme: theme, copy: copy }) }), data.addons.length > 0 || copy.finePrint.length > 0 ? (_jsx("div", { style: { marginTop: 40 }, children: _jsx(FinePrint, { theme: theme, copy: copy, data: data }) })) : null] }));
}
function Section({ children, theme, id, }) {
    return (_jsx("section", { id: id, style: {
            background: theme.bg,
            color: theme.text,
            padding: '48px 20px',
        }, children: _jsx("div", { style: { maxWidth: 1120, margin: '0 auto' }, children: children }) }));
}
function buildFreeSitesLine(freeSites, copy) {
    if (freeSites === null)
        return null;
    // Keep the deck's subline but substitute "N sites on Free" if it
    // contains the "{free_sites}" token; otherwise prepend.
    const base = copy.subline;
    const unit = freeSites === 1 ? 'site' : 'sites';
    if (base.includes('{free_sites}')) {
        return base.replace('{free_sites}', String(freeSites));
    }
    return `Every account gets ${freeSites} ${unit} on Free. ${base}`;
}
/* PricingTableData / PricingPlan / … already re-exported at the top
 * of this file. The style block below is no-op — kept here only to
 * keep CSS-in-JS inline styles readable from the DOM. */
export const PRICING_SURFACE_STYLE = {};
//# sourceMappingURL=PricingTable.js.map